import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { SavedArtworkModel, SavedArtworkRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['savedArtworks']

type SavedArtworkFilters = {
  agency_id?: number,
  string?: string,
  user_id?: number,
}

type UseSavedArtworksOptions = {
  filters?: SavedArtworkFilters,
  performHttpRequests?: boolean,
  requestOptions?: SavedArtworkRequestOptions,
}

function useSavedArtworks(options: UseSavedArtworksOptions) {
  const { filters = {}, requestOptions } = options
  const {
    agency_id: filterAgencyId,
    string: filterString,
    user_id: filterUserId,
  } = filters

  const {
    updatedEntities: { savedArtworks: savedArtworksUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { savedArtworks } = useSelector(reduxState => reduxState.entities)

  const filteredSavedArtworks = useMemo(() => {
    const filtered = Object.values(savedArtworks).filter((savedArtwork: SavedArtworkModel) => {
      const {
        agency_id, id, title, user_id,
      } = savedArtwork

      const matchAgencyId = matchFilterNumber(agency_id, filterAgencyId)

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchUserId = matchFilterNumber(user_id, filterUserId)

      return matchAgencyId && matchString && matchUserId
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [savedArtworksUpdatedAt, JSON.stringify(filters)])

  const filteredSavedArtworksCount = filteredSavedArtworks.length
  const hasFilteredSavedArtworks = !!filteredSavedArtworksCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredSavedArtworksCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingSavedArtworks } = useReduxAction(
    'savedArtworks',
    'loadSavedArtworks',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredSavedArtworks,
    filteredSavedArtworksCount,
    hasFilteredSavedArtworks,
    loading: loadingSavedArtworks,
  }
}

export default useSavedArtworks
